import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Video, Expand, BadgeCheck, Share2 } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { buildWhatsAppLink, enquireMessage } from "@/lib/whatsapp";
import { MediaTile } from "@/components/MediaTile";
import { MediaLightbox } from "@/components/MediaLightbox";
import { toast } from "sonner";

/**
 * Generic product card used on the full catalog page (/all-items).
 * Works uniformly across every category (gold, silver, diamond, others) —
 * unlike ProductCard/SilverImageCard which are styled specifically for
 * their own homepage sections.
 */
export const CatalogItemCard = ({ item }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const link = buildWhatsAppLink(enquireMessage(item.name));
  const extraMediaCount = (item.gallery_images?.length || 0) + (item.gallery_videos?.length || 0);

  const handleShare = async (e) => {
    e.preventDefault();
    const url = `${window.location.origin}/product/${item.id}`;
    const shareText = `Check out ${item.name} at Vini Gems And Jewellers`;
    if (navigator.share) {
      try {
        await navigator.share({ title: item.name, text: shareText, url });
      } catch {
        // User cancelled the native share sheet — no action needed.
      }
    } else {
      window.open(buildWhatsAppLink(`${shareText}\n${url}`), "_blank", "noopener,noreferrer");
      toast.success("Opening WhatsApp to share...");
    }
  };

  return (
    <div className="group card-lux rounded-2xl overflow-hidden flex flex-col h-full" data-testid={`catalog-item-card-${item.id}`}>
      <button
        type="button"
        onClick={() => setLightboxOpen(true)}
        className="relative aspect-square overflow-hidden bg-muted text-left w-full"
        aria-label={`View all photos of ${item.name}`}
        data-testid={`catalog-item-media-btn-${item.id}`}
      >
        <MediaTile
          src={item.media_url}
          mediaType={item.media_type}
          alt={item.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <span className="absolute top-3 left-3 rounded-full bg-background/85 backdrop-blur px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-primary border border-primary/30 capitalize">
          {item.category}
        </span>

        {item.purity && (
          <span className="absolute top-3 right-3 rounded-full bg-primary text-primary-foreground px-3 py-1 text-[11px] font-semibold">
            {item.purity}
          </span>
        )}

        {item.media_type === "video" && (
          <span className="absolute bottom-3 right-3 h-8 w-8 rounded-full bg-background/80 backdrop-blur flex items-center justify-center border border-primary/30">
            <Video className="h-3.5 w-3.5 text-primary" />
          </span>
        )}

        {extraMediaCount > 0 && (
          <span className="absolute bottom-3 left-3 rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[11px] font-medium text-foreground border border-primary/30 flex items-center gap-1">
            <Expand className="h-3 w-3 text-primary" /> +{extraMediaCount} more
          </span>
        )}
      </button>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <Link
            to={`/product/${item.id}`}
            className="font-heading text-lg text-foreground font-semibold hover:text-primary transition-colors"
            data-testid={`catalog-item-name-link-${item.id}`}
          >
            {item.name}
          </Link>
          <button
            type="button"
            onClick={handleShare}
            className="flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            aria-label={`Share ${item.name}`}
            data-testid={`catalog-item-share-btn-${item.id}`}
          >
            <Share2 className="h-3.5 w-3.5" />
          </button>
        </div>
        {item.tag && <p className="mt-1 text-sm text-muted-foreground flex-1">{item.tag}</p>}
        {!item.tag && <div className="flex-1" />}

        {!item.purity && (
          <span className="mt-2 inline-flex items-center text-xs text-muted-foreground">
            <BadgeCheck className="h-3.5 w-3.5 mr-1 text-primary" /> Purity &mdash; ask on WhatsApp
          </span>
        )}

        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(buttonVariants({ variant: "whatsapp", size: "sm" }), "mt-4 w-full")}
          data-testid={`catalog-item-enquire-btn-${item.id}`}
        >
          <i className="fa-brands fa-whatsapp" /> Enquire on WhatsApp
        </a>
      </div>

      <MediaLightbox item={item} open={lightboxOpen} onOpenChange={setLightboxOpen} />
    </div>
  );
};
